import React from "react";
import { Box, Paper, Typography, Button, alpha } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { brandThemeOptions } from "./brandColors";
import InsuranceIDCard from "./InsuranceIDCard";
import DocumentDetailsCard from "./DocumentDetailsCard";
import PolicyDetailsCard from "./PolicyDetailsCard";
import PolicyReviewCard from "./PolicyReviewCard";
import CoverageDetailsCard from "./CoverageDetailsCard";
import ClaimStatusCard from "./ClaimStatusCard";
import TrackClaimCard from "./TrackClaimCard";
import BillingDetailsCard from "./BillingDetailsCard";
import BillSummaryCard from "./BillSummaryCard";
import AgentDetailsCard from "./AgentDetailsCard";
import AddVehiclePanel from "./AddVehiclePanel";
import AddDriverPanel from "./AddDriverPanel";
import EditDriverPanel from "./EditDriverPanel";
import FileAClaimPanel from "./FileAClaimPanel";
import ChangeGarageLocationCard from "./ChangeGarageLocationCard";
import DriverDetailsCard from "./DriverDetailsCard";
import Payment from "../../Payment/Payment";
import { IPolicyDriver } from "../../../models/user/policyDetails/PolicyDetails";
import ServiceRequestsPanel from "./ServiceRequestsPanel";
import DeleteVehiclePanel from "./DeleteVehiclePanel";
import HealthScoreCard from "./HealthScoreCard";
import WellnessCard from "./WellnessCard";


interface PolicyAction {
    id: string;
    label: string;
    params?: any;
    followup_message?: string;
}

interface PolicySummaryPanelProps {
    card: any;
    onAction?: (action: PolicyAction) => void;
    policyId?: string;
    carrier?: string;
    lobCode?: string;
    system?: string;
    policyNumber?: string;
}

const PolicySummaryPanel: React.FC<PolicySummaryPanelProps> = ({
    card,
    onAction,
    policyId,
    carrier,
    lobCode,
    system,
    policyNumber,
}) => {    
    if (!card) return null;

    if (card.type === "service_request_details" || card.type === "service_request") {
        const refs = card.entity_refs || {};
        return (
            <ServiceRequestsPanel
                policyNumber={refs.policy_number || card.policyId}
                carrier={carrier || refs.carrier || card.carrier}
                lobCode={lobCode || refs.lob_code || card.lobCode}
                system={system || refs.system || card.system}
                type={card.type}
            />
        );
    }

    if (card.type === "id_card") {
        return <InsuranceIDCard card={card} onAction={(action) => onAction?.(action as any)} />;
    }
    if (card.type === "document_details" ) {
        return <DocumentDetailsCard card={card} onAction={(action) => onAction?.(action as any)} />;
    }
  if (card.type === "bill_summary") {
        return <BillSummaryCard card={card} onAction={(action) => onAction?.(action as any)} />;
    }
    if (card.type === "file_claim") {
        const refs = card.entity_refs || {};
        return (
            <FileAClaimPanel
                policyId={policyId || refs.policy_id || card.policyId}
                carrier={carrier || refs.carrier || card.carrier}
                lobCode={lobCode || refs.lob_code || card.lobCode}
                system={system || refs.system || card.system}
                policyNumber={policyNumber || refs.policy_number || card.policy_number}
                onAction={(action) => onAction?.(action as any)}
            />
        );
    }
     if (card.type === "initiate_payment") {
        const refs = card.entity_refs || {};
       
        // Clean the due_amount by removing currency symbols and extra spaces
        const cleanAmount = (amount: string | undefined): string => {
            if (!amount) return "0";
            // Remove dollar signs, commas, and extra spaces, then extract numeric value
            const cleaned = amount.replace(/[$,\s]/g, '').trim();
            return cleaned || "0";
        };
       
        const dueAmount = refs.due_amount || card.due_amount;
        const cleanedAmount = cleanAmount(dueAmount);
       
        return (
            <Payment
                carrierPolicyNumber={refs.policy_number || card.policy_number || policyNumber}
                amount={cleanedAmount}
            />
        );
    }

  if (card.type === "policy_details") {
        return <PolicyDetailsCard card={card} onAction={(action) => onAction?.(action as any)} />;
    }

    if (card.type === "policy_review") {
        return <PolicyReviewCard card={card} onAction={(action) => onAction?.(action as any)} />;
    }

    if (card.type === "coverage_details" || card.type === "coverage_compare") {
        const refs = card.entity_refs || {};
    return <CoverageDetailsCard card={card} onAction={(action) => onAction?.(action as any)} policyNumber={refs.policy_number} />;
  }
    if (card.type === "claim_status") {
        return <ClaimStatusCard card={card} onAction={(action) => onAction?.(action as any)} />;
    }
    if (card.type === "track_claim") {
        return <TrackClaimCard card={card} onAction={(action) => onAction?.(action as any)} />;
    }
    if (card.type === "billing_details") {
        return <BillingDetailsCard card={card} onAction={(action) => onAction?.(action as any)} />;
    }
    if (card.type === "agent_details") {
        return <AgentDetailsCard card={card} onAction={(action) => onAction?.(action as any)} />;
    }
    if (card.type === "delete_vehicle") {
        return <DeleteVehiclePanel card={card} onAction={(action) => onAction?.(action as any)} />;
    }
    if (card.type === "add_vehicle") {
        const refs = card.entity_refs || {};
        return (
            <AddVehiclePanel
                policyId={policyId || refs.policy_id || card.policyId}
                carrier={carrier || refs.carrier || card.carrier}
                lobCode={lobCode || refs.lob_code || card.lobCode}
                system={system || refs.system || card.system}
                policyNumber={refs.policy_number || card.policyNumber}
                onAction={(action) => onAction?.(action as any)}
            />
        );
    }
    if (card.type === "add_driver") {
        const refs = card.entity_refs || {};
        return (
            <AddDriverPanel
                policyId={policyId || refs.policy_id || card.policyId}
                carrier={carrier || refs.carrier || card.carrier}
                lobCode={lobCode || refs.lob_code || card.lobCode}
                system={system || refs.system || card.system}
                policyNumber={refs.policy_number || card.policyNumber}
                onAction={(action) => onAction?.(action as any)}
            />
        );
    }

    if (card.type === "edit_driver" || card.type === "update_driver") {
        const refs = card.entity_refs || {};
        // Extract driver data from card
        const driverData: Partial<IPolicyDriver> = card.driver_data || {};
        return (
            <EditDriverPanel
                policyId={policyId || refs.policy_id || card.policyId}
                carrier={carrier || refs.carrier || card.carrier}
                lobCode={lobCode || refs.lob_code || card.lobCode}
                system={system || refs.system || card.system}
                policyNumber={refs.policy_number || card.policyNumber}
                driver={driverData}
                onAction={(action) => onAction?.(action as any)}
            />
        );
    }

    if (card.type === "update_garage_location" || card.type === "change_garage_location") {
        const refs = card.entity_refs || {};
        return (
            <ChangeGarageLocationCard
                policyId={policyId || refs.policy_id || card.policyId}
                carrier={carrier || refs.carrier || card.carrier}
                lobCode={lobCode || refs.lob_code || card.lobCode}
                system={system || refs.system || card.system}
                policyNumber={refs.policy_number || card.policyNumber}
                onAction={(action) => onAction?.(action as any)}
                card={card}
            />
        );
    }

    if (card.type === "driver_details" || card.type === "delete_driver") {
        return <DriverDetailsCard card={card} onAction={(action) => onAction?.(action as any)} />;
    }

    if (card.type === "health_score") {
        return <HealthScoreCard card={card} onAction={(action) => onAction?.(action as any)} />;
    }
    if (card.type === "wellness" || card.type === "health_recommendation" || card.type === "consult" 
        || card.type === "tests" 
        || card.type === "card_list_kotak"
        || card.type === "instant_consultation"
        || card.type === "schedule_consultation"
        || card.type === "book_full_body_test"
        || card.type === "book_blood_test"
        || card.type === "wellness"
        || card.type === "guided_session"
        || card.type === "affirmations"
        || card.type === "breathing_reset"
        || card.type === "policy_summary_kotak"
    
    
    ) {
        return <WellnessCard card={card} onAction={(action) => onAction?.(action as any)} />;
    }
    const handleItemAction = (action?: PolicyAction) => {
        if (!action) return;
        if (onAction) onAction(action);
    };

    const handleCtaAction = (action: PolicyAction) => {
        if (onAction) onAction(action);
    };

    return (
        <ThemeProvider theme={(outer) => createTheme(outer, brandThemeOptions)}>
        <Paper
            elevation={0}
            sx={{
                width: { xs: "100%", md: "100%" },
                border: "1px solid #eceff3",
                borderRadius: { xs: 2, md: 2.5 },
                p: { xs: 1.25, md: 1.75 },
                bgcolor: "#ffffff",
                maxHeight: { xs: "auto", md: "auto" },
                order: { xs: -1, md: 0 },
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                        sx={{
                            width: { xs: 26, md: 30 },
                            height: { xs: 26, md: 30 },
                            borderRadius: "9px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "primary.main",
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                            flexShrink: 0,
                        }}
                    >
                        <DescriptionOutlinedIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: { xs: 12.5, md: 14 }, color: "#111827" }}>
                            {card?.title || "Your Policies"}
                        </Typography>
                        {card?.subtitle && (
                            <Typography sx={{ fontSize: { xs: 10.5, md: 12 }, color: "#6b7280" }}>
                                {card.subtitle}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {(card?.sections || []).map((section: any, sIdx: number) => (
                    <Box
                        key={`section_${sIdx}`}
                        sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                        {section?.heading && (
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: 11, md: 12 },
                                    color: "#374151",
                                }}
                            >
                                {section.heading}
                            </Typography>
                        )}
                        {(section?.items || []).map((item: any, iIdx: number) => (
                            <Paper
                                elevation={0}
                                key={`item_${iIdx}`}
                                sx={{
                                    position: "relative",
                                    overflow: "hidden",
                                    border: "1px solid #eceff3",
                                    borderRadius: { xs: 2, md: 2.5 },
                                    p: { xs: 1, md: 1.25 },
                                    pl: { xs: 1.5, md: 1.75 },
                                    bgcolor: "#ffffff",
                                    transition:
                                        "background-color 150ms ease, border-color 200ms ease, box-shadow 200ms ease, transform 200ms ease",
                                    cursor: "pointer",
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "4px",
                                        bgcolor: "primary.main",
                                    },
                                    "&:hover": {
                                        transform: "translateY(-1px)",
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
                                        boxShadow: (theme) =>
                                            `0 8px 22px ${alpha(theme.palette.primary.main, 0.14)}`,
                                    },
                                    "&:hover .policy-action-btn": {
                                        bgcolor: "primary.main",
                                        color: "primary.contrastText",
                                        borderColor: "primary.main",
                                        boxShadow: (theme) =>
                                            `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 11, md: 12.5 },
                                                color: "primary.main",
                                                fontWeight: 600,
                                            }}
                                        >
                                            {item?.label}
                                        </Typography>
                                        {item?.value && (
                                            <Typography
                                                sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}
                                            >
                                                {item.value}
                                            </Typography>
                                        )}
                                    </Box>
                                    {item?.action && (
                                        <Button
                                            className="policy-action-btn"
                                            size="small"
                                            variant="outlined"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleItemAction(item.action);
                                            }}
                                            sx={{
                                                fontSize: { xs: 10, md: 12 },
                                                fontWeight: 600,
                                                px: { xs: 1.25, md: 1.5 },
                                                py: "5px",
                                                borderRadius: 9999,
                                                whiteSpace: "nowrap",
                                                borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
                                                color: "primary.main",
                                                transition:
                                                    "background-color 150ms ease, color 150ms ease, box-shadow 150ms ease, border-color 150ms ease",
                                                textTransform: "none",
                                            }}
                                        >
                                            {item.action?.label || "View"}
                                        </Button>
                                    )}
                                </Box>

                                {(item?.ctas || []).length > 0 && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: 0.5,
                                            flexWrap: "wrap",
                                            pt: 0.75,
                                        }}
                                    >
                                        {(item.ctas || []).map((cta: any, cIdx: number) => (
                                            <Button
                                                key={`cta_${iIdx}_${cIdx}`}
                                                size="small"
                                                variant="contained"
                                                disableElevation
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCtaAction(cta);
                                                }}
                                                sx={{
                                                    fontSize: { xs: 10, md: 12 },
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                    borderRadius: 9999,
                                                    py: { xs: 0.5, md: 0.75 },
                                                    px: { xs: 1.5, md: 2 },
                                                }}
                                            >
                                                {cta?.label}
                                            </Button>
                                        ))}
                                    </Box>
                                )}
                            </Paper>
                        ))}
                    </Box>
                ))}

                {(card?.cta?.secondary || []).length > 0 && (
                    <Box sx={{ borderTop: "1px solid #e5e7eb", pt: 1, mt: 0.5 }}>
                        <Typography
                            sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280", mb: 0.5 }}
                        >
                            Quick Actions
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                gap: 0.75,
                            }}
                        >
                            {card.cta.secondary.map((cta: any, idx: number) => (
                                <Button
                                    key={`secondary_${idx}`}
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => handleCtaAction(cta)}
                                    sx={{
                                        fontSize: { xs: 10, md: 12 },
                                        fontWeight: 600,
                                        textTransform: "none",
                                        borderRadius: 9999,
                                        py: { xs: 0.75, md: 0.9 },
                                        // borderColor: (theme) => alpha(theme.palette.secondary.main, 0.6),
                                        // color: "secondary.main",
                                        // "&:hover": {
                                        //     borderColor: "secondary.main",
                                        //     bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                                        // },
                                    }}
                                >
                                    {cta.label}
                                </Button>
                            ))}
                        </Box>
                    </Box>
                )}

                {(card?.disclaimers || []).length > 0 && (
                    <Typography
                        sx={{ fontSize: { xs: 10, md: 11 }, color: "#6b7280", mt: 1 }}
                    >
                        {card.disclaimers.join(" ")}
                    </Typography>
                )}
            </Box>
        </Paper>
        </ThemeProvider>
    );
};

export default PolicySummaryPanel;